import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  KeyRound,
  Gauge,
  AlertTriangle,
  Server,
  FileSpreadsheet,
  FileMinus,
  Search,
  PlayCircle,
  FileText,
  Boxes,
} from "lucide-react";
import type { ComponentType } from "react";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  openApiSpec,
  PUBLIC_API_BASE_URL,
  type HttpMethod,
  type OperationObject,
  type ParameterObject,
  type SchemaObject,
} from "@/api/openapi-public";

export const Route = createFileRoute("/_authenticated/documentacion-api/")({
  component: ApiDocsPage,
});

const spec = openApiSpec;

// --- Resolución de $ref y schemas ---------------------------------------------

function refName(ref: string): string {
  const parts = ref.split("/");
  return parts[parts.length - 1];
}

function deref(schema: SchemaObject): SchemaObject {
  if (schema.$ref) return spec.components.schemas[refName(schema.$ref)] ?? schema;
  return schema;
}

interface TypeLabel {
  label: string;
  refName?: string;
  enumValues?: string[];
}

/** Etiqueta legible del tipo de un schema (resuelve $ref, arrays y enums). */
function typeLabel(schema: SchemaObject): TypeLabel {
  if (schema.$ref) {
    const name = refName(schema.$ref);
    const target = spec.components.schemas[name];
    // Un $ref a un enum se muestra como el nombre del enum + valores.
    if (target?.enum) return { label: name, refName: name, enumValues: target.enum };
    return { label: name, refName: name };
  }
  if (schema.enum) return { label: "enum", enumValues: schema.enum };
  if (schema.allOf) return { label: "object" };
  if (schema.type === "array" && schema.items) {
    const inner = typeLabel(schema.items);
    return { label: `${inner.label}[]`, refName: inner.refName };
  }
  const base = schema.type ?? "object";
  return { label: schema.format ? `${base} <${schema.format}>` : base };
}

interface FieldRow {
  name: string;
  schema: SchemaObject;
  required: boolean;
}

/** Aplana las propiedades de un schema de objeto (mezclando `allOf` y `$ref`). */
function objectFields(schema: SchemaObject): FieldRow[] {
  let props: Record<string, SchemaObject> = {};
  let required: string[] = [];
  const collect = (sc: SchemaObject) => {
    const d = sc.$ref ? deref(sc) : sc;
    if (d.allOf) d.allOf.forEach(collect);
    if (d.properties) props = { ...props, ...d.properties };
    if (d.required) required = [...required, ...d.required];
  };
  collect(schema);
  return Object.entries(props).map(([name, sc]) => ({
    name,
    schema: sc,
    required: required.includes(name),
  }));
}

/** Nombres de los sub-modelos de objeto referenciados por el contrato. */
const OBJECT_MODELS = ["Party", "Guarantee", "Obligation"] as const;
const ENUM_MODELS = [
  "RegistrationStatus",
  "PersonType",
  "CompanyType",
  "PrincipalCreditor",
  "GuaranteeType",
  "AssetType",
  "ExecutionStatus",
  "ExecutionType",
  "ExecutionItemStatus",
] as const;

const METHOD_VARIANT: Record<string, "success" | "warning" | "default"> = {
  get: "success",
  post: "warning",
};

/**
 * Un icono por grupo de endpoints. Son los MISMOS del menú lateral (`app-shell.tsx`), para
 * que un grupo se reconozca igual en las dos pantallas. Hay fallback, así que un tag nuevo
 * no rompe la página; solo saldría con el icono genérico.
 */
const TAG_ICON: Record<string, ComponentType<{ className?: string }>> = {
  registrations: FileSpreadsheet,
  deregistrations: FileMinus,
  inquiries: Search,
  executions: PlayCircle,
  receipts: FileText,
};

// --- Ejemplos: curl y respuesta -----------------------------------------------

function sampleForParam(name: string): string {
  if (name === "id" || name === "registrationId" || name === "executionId")
    return "3f9a1c2e-7b6d-4e51-9a12-8c0d5e6f7a8b";
  if (name === "contractNumber") return "CTR-2026-00123";
  if (name === "batchId") return "b1a2c3d4-e5f6-4789-a012-b3c4d5e6f7a8";
  // El Nº de Garantía del RUG es numérico y corto; un `<folioRug>` en el curl de ejemplo
  // no le dice al integrador qué forma tiene el dato.
  if (name === "folioRug") return "789012";
  return `<${name}>`;
}

function buildCurl(method: HttpMethod, path: string, op: OperationObject): string {
  let url = PUBLIC_API_BASE_URL + path;
  const params = op.parameters ?? [];
  for (const p of params) {
    if (p.in === "path") url = url.replace(`{${p.name}}`, sampleForParam(p.name));
  }
  const requiredQuery = params.filter((p) => p.in === "query" && p.required);
  if (requiredQuery.length > 0) {
    const qs = requiredQuery
      .map((p) => `${p.name}=${encodeURIComponent(sampleForParam(p.name))}`)
      .join("&");
    url = `${url}?${qs}`;
  }

  const lines = [`curl -X ${method.toUpperCase()} '${url}' \\`, `  -H 'X-Api-Key: <tu-api-key>'`];
  const body = op.requestBody?.content["application/json"];
  if (body) {
    lines[lines.length - 1] += " \\";
    lines.push(`  -H 'Content-Type: application/json' \\`);
    const example = body.example ?? {};
    const json = JSON.stringify(example, null, 2)
      .split("\n")
      .map((l, i) => (i === 0 ? l : `  ${l}`))
      .join("\n");
    lines.push(`  -d '${json}'`);
  }
  return lines.join("\n");
}

function responseExample(op: OperationObject): string | null {
  // La respuesta de éxito no siempre es 200: las altas de consultas y de bajas
  // responden 201, y buscar solo el 200 dejaba esos endpoints SIN ejemplo de
  // respuesta en la pantalla, que es justo lo que más se mira al integrar.
  const okCode = Object.keys(op.responses).find((c) => c.startsWith("2"));
  const media = okCode ? op.responses[okCode]?.content?.["application/json"] : undefined;
  if (!media) return null;
  const value = media.example ?? {};
  return JSON.stringify(value, null, 2);
}

// --- Componentes de UI ---------------------------------------------------------

function CodeBlock({ children }: { children: string }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border bg-muted/40">
      <pre className="p-3 text-xs leading-relaxed font-mono whitespace-pre">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function TypeCell({ schema }: { schema: SchemaObject }) {
  const info = typeLabel(schema);
  return (
    <span className="font-mono text-xs">
      {info.label}
      {info.enumValues ? (
        <span className="block text-[10px] text-muted-foreground">
          {info.enumValues.join(" | ")}
        </span>
      ) : null}
    </span>
  );
}

function FieldsTable({ schema }: { schema: SchemaObject }) {
  const { t } = useTranslation();
  const target = schema.$ref ? deref(schema) : schema;

  // Respuesta como arreglo plano (p. ej. /search): describe el item.
  if (target.type === "array" && target.items) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          {t("apiDocs.arrayOf")}{" "}
          <span className="font-mono">{typeLabel(target.items).label}</span>
        </p>
        <FieldsTable schema={target.items} />
      </div>
    );
  }

  const fields = objectFields(target);
  if (fields.length === 0) {
    return <TypeCell schema={target} />;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("apiDocs.fieldsTable.field")}</TableHead>
            <TableHead>{t("apiDocs.fieldsTable.type")}</TableHead>
            <TableHead>{t("apiDocs.fieldsTable.required")}</TableHead>
            <TableHead>{t("apiDocs.fieldsTable.description")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((f) => (
            <TableRow key={f.name}>
              <TableCell className="font-mono text-xs whitespace-nowrap">{f.name}</TableCell>
              <TableCell>
                <TypeCell schema={f.schema} />
              </TableCell>
              <TableCell className="text-xs">
                {f.required ? (
                  <Badge variant="secondary">{t("apiDocs.yes")}</Badge>
                ) : (
                  <span className="text-muted-foreground">{t("apiDocs.no")}</span>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {deref(f.schema).description ?? f.schema.description ?? ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ParametersTable({ params }: { params: ParameterObject[] }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("apiDocs.paramsTable.name")}</TableHead>
            <TableHead>{t("apiDocs.paramsTable.in")}</TableHead>
            <TableHead>{t("apiDocs.paramsTable.required")}</TableHead>
            <TableHead>{t("apiDocs.paramsTable.type")}</TableHead>
            <TableHead>{t("apiDocs.paramsTable.description")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {params.map((p) => (
            <TableRow key={`${p.in}-${p.name}`}>
              <TableCell className="font-mono text-xs whitespace-nowrap">{p.name}</TableCell>
              <TableCell className="text-xs">
                <Badge variant="outline">{p.in}</Badge>
              </TableCell>
              <TableCell className="text-xs">
                {p.required ? (
                  <Badge variant="secondary">{t("apiDocs.yes")}</Badge>
                ) : (
                  <span className="text-muted-foreground">{t("apiDocs.no")}</span>
                )}
              </TableCell>
              <TableCell>
                <TypeCell schema={p.schema} />
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{p.description ?? ""}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EndpointCard({
  path,
  method,
  op,
}: {
  path: string;
  method: HttpMethod;
  op: OperationObject;
}) {
  const { t } = useTranslation();
  const params = op.parameters ?? [];
  const requestSchema = op.requestBody?.content["application/json"]?.schema;
  const responseSchema = op.responses["200"]?.content?.["application/json"]?.schema;
  const curl = buildCurl(method, path, op);
  const respExample = responseExample(op);
  // Se descarta TODO el 2xx, no solo el 200: las altas de consultas y de bajas
  // responden 201, y filtrar únicamente el 200 las pintaba como si 201 fuera un
  // código de error.
  const errorCodes = Object.keys(op.responses).filter((c) => !c.startsWith("2"));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={METHOD_VARIANT[method] ?? "default"} className="uppercase">
            {method}
          </Badge>
          <code className="font-mono text-sm break-all">{path}</code>
        </div>
        <CardTitle className="text-sm font-semibold mt-1">{op.summary}</CardTitle>
        {op.description ? (
          <p className="text-xs text-muted-foreground">{op.description}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-5">
        {params.length > 0 ? (
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("apiDocs.params")}
            </h4>
            <ParametersTable params={params} />
          </section>
        ) : null}

        {requestSchema ? (
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("apiDocs.request")}
            </h4>
            <FieldsTable schema={requestSchema} />
          </section>
        ) : null}

        {responseSchema ? (
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("apiDocs.response")}
            </h4>
            <FieldsTable schema={responseSchema} />
          </section>
        ) : null}

        {errorCodes.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("apiDocs.errorCodes")}:{" "}
            {errorCodes.map((c) => (
              <Badge key={c} variant="outline" className="ml-1 font-mono">
                {c}
              </Badge>
            ))}
          </p>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("apiDocs.example.request")}
            </h4>
            <CodeBlock>{curl}</CodeBlock>
          </section>
          {respExample ? (
            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("apiDocs.example.response")}
              </h4>
              <CodeBlock>{respExample}</CodeBlock>
            </section>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

// --- Página --------------------------------------------------------------------

interface EndpointEntry {
  path: string;
  method: HttpMethod;
  op: OperationObject;
}

function ApiDocsPage() {
  const { t } = useTranslation();

  const endpointsByTag = useMemo(() => {
    const map = new Map<string, EndpointEntry[]>();
    for (const [path, item] of Object.entries(spec.paths)) {
      for (const method of Object.keys(item) as HttpMethod[]) {
        const op = item[method];
        if (!op) continue;
        const tag = op.tags[0] ?? "otros";
        const list = map.get(tag) ?? [];
        list.push({ path, method, op });
        map.set(tag, list);
      }
    }
    return map;
  }, []);

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(spec, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mstar-public-api.openapi.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Tabla FIJA, no derivada del spec: cada operación declara solo los códigos que
  // ella devuelve, así que recorrerlas nunca produciría la lista completa (el 429 y
  // el 5xx no cuelgan de ninguna operación en particular, y el 409 solo de algunas).
  // Al agregar un código aquí, agrega su texto en los tres idiomas.
  const errorRows = [
    { code: "400", key: "e400" },
    { code: "401", key: "e401" },
    { code: "404", key: "e404" },
    { code: "409", key: "e409" },
    { code: "429", key: "e429" },
    { code: "5xx", key: "e5xx" },
  ];

  return (
    <>
      <PageHeader
        title={t("apiDocs.title")}
        subtitle={t("apiDocs.subtitle")}
        actions={
          <Button onClick={handleDownload}>
            <Download className="size-4" />
            {t("apiDocs.download")}
          </Button>
        }
      />

      <div className="space-y-8 max-w-5xl">
        {/* Resumen / cabecera del contrato */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {spec.info.title} · v{spec.info.version}
            </CardTitle>
            <p className="text-xs text-muted-foreground">{spec.info.description}</p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Server className="size-4 text-primary" />
                <h3 className="text-sm font-semibold">{t("apiDocs.overview.baseUrl")}</h3>
              </div>
              <CodeBlock>{PUBLIC_API_BASE_URL}</CodeBlock>
              <p className="text-xs text-muted-foreground">{t("apiDocs.overview.baseUrlNote")}</p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <KeyRound className="size-4 text-primary" />
                <h3 className="text-sm font-semibold">{t("apiDocs.overview.auth")}</h3>
              </div>
              <p className="text-xs text-muted-foreground">{t("apiDocs.overview.authDesc")}</p>
              <CodeBlock>X-Api-Key: &lt;tu-api-key&gt;</CodeBlock>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Gauge className="size-4 text-primary" />
                <h3 className="text-sm font-semibold">{t("apiDocs.overview.limits")}</h3>
              </div>
              <p className="text-xs text-muted-foreground">{t("apiDocs.overview.limitsDesc")}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-4 text-primary" />
                <h3 className="text-sm font-semibold">{t("apiDocs.overview.errors")}</h3>
              </div>
              <p className="text-xs text-muted-foreground">{t("apiDocs.overview.errorsDesc")}</p>
              <CodeBlock>{'{ "message": "string", "requestId": "string" }'}</CodeBlock>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("apiDocs.errorsTable.code")}</TableHead>
                      <TableHead>{t("apiDocs.errorsTable.meaning")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errorRows.map((r) => (
                      <TableRow key={r.code}>
                        <TableCell className="font-mono text-xs">{r.code}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {t(`apiDocs.errorsTable.${r.key}`)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Endpoints por tag */}
        {spec.tags.map((tag) => {
          const entries = endpointsByTag.get(tag.name) ?? [];
          const Icon = TAG_ICON[tag.name] ?? Boxes;
          return (
            <section key={tag.name} className="space-y-4">
              <div className="flex items-center gap-2">
                <Icon className="size-5 text-primary" />
                <h2 className="text-lg font-semibold capitalize">{tag.name}</h2>
              </div>
              {tag.description ? (
                <p className="text-sm text-muted-foreground -mt-2">{tag.description}</p>
              ) : null}
              <div className="space-y-4">
                {entries.map((e) => (
                  <EndpointCard
                    key={`${e.method}-${e.path}`}
                    path={e.path}
                    method={e.method}
                    op={e.op}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {/* Modelos de datos reutilizables */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Boxes className="size-5 text-primary" />
            <h2 className="text-lg font-semibold">{t("apiDocs.models.title")}</h2>
          </div>
          <div className="space-y-4">
            {OBJECT_MODELS.map((name) => (
              <Card key={name}>
                <CardHeader>
                  <CardTitle className="text-sm font-mono">{name}</CardTitle>
                  {spec.components.schemas[name].description ? (
                    <p className="text-xs text-muted-foreground">
                      {spec.components.schemas[name].description}
                    </p>
                  ) : null}
                </CardHeader>
                <CardContent>
                  <FieldsTable schema={spec.components.schemas[name]} />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <h3 className="text-sm font-semibold">{t("apiDocs.models.enums")}</h3>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("apiDocs.fieldsTable.field")}</TableHead>
                      <TableHead>{t("apiDocs.models.values")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ENUM_MODELS.map((name) => (
                      <TableRow key={name}>
                        <TableCell className="font-mono text-xs whitespace-nowrap align-top">
                          {name}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="flex flex-wrap gap-1">
                            {(spec.components.schemas[name].enum ?? []).map((v) => (
                              <Badge key={v} variant="outline" className="font-mono">
                                {v}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
}
